import type { FieldErrors } from "react-hook-form";
import {
  AREA_KEY,
  BLOCK_KEY,
  CITY_KEY,
  EAddressType,
  FLAT_KEY,
  FLOOR_KEY,
  ZONE_KEY,
} from "../../../constants";
import type { EStoreIds } from "../../../constants";
import type { IAddress, TranslationFn } from "../../../types";
import type {
  IUseAddressFormSetters,
  IUseAddressFormStates,
} from "../../../hooks";
import { getFormFieldRequirements } from "../../../utils";
import {
  validateAddressType,
  validateFormFields,
  validateQatarFields,
  validateReceiverDetails,
} from "./address-form.validators";

export interface IFormValidations {
  areFieldsValid: boolean;
  areQatarFieldsValid?: boolean;
  areReceiverDetailsValid: boolean;
  isAddressTypeValid: boolean;
}

export const getPhoneNumberError = (
  errors: FieldErrors<IAddress>,
  invalidPhoneNumber: boolean,
  t: TranslationFn,
): string => {
  if (errors.phone) return t("addressJourney.error.phoneNumber.required");
  if (invalidPhoneNumber) return t("addressJourney.error.phoneNumber.invalid");
  return "";
};

export const validateForm = (
  addressFormSetters: IUseAddressFormSetters,
  addressFormStates: IUseAddressFormStates,
  formData: IAddress,
  storeId: EStoreIds,
  t: TranslationFn,
): boolean => {
  const { isQatarFormFieldRequired } = getFormFieldRequirements(storeId);
  const validations: IFormValidations = {
    isAddressTypeValid: validateAddressType(
      addressFormSetters,
      addressFormStates,
      t,
    ),
    areFieldsValid: validateFormFields(
      formData,
      addressFormSetters,
      addressFormStates,
      storeId,
      t,
    ),
    areReceiverDetailsValid: validateReceiverDetails(
      formData,
      addressFormSetters,
    ),
  };

  if (isQatarFormFieldRequired) {
    validations.areQatarFieldsValid = validateQatarFields(
      addressFormSetters,
      addressFormStates,
    );
  }
  return Object.values(validations).every(Boolean);
};

export const getFormattedAddressType = (
  addressType: string[],
): EAddressType[] => {
  if (addressType.length === 0) {
    return [EAddressType.HOME];
  }

  const typeToCheck = addressType[0].toLowerCase();

  const addressTypeMap: Record<string, EAddressType> = {
    [EAddressType.HOME.toString().toLowerCase()]: EAddressType.HOME,
    [EAddressType.OFFICE.toString().toLowerCase()]: EAddressType.OFFICE,
  };

  return [addressTypeMap[typeToCheck] ?? EAddressType.OTHER];
};

export const parseMakaniValue = (
  makani: string,
): {
  zone: string | undefined;
  area: string | undefined;
  city: string | undefined;
} => {
  const parts = makani.split("|").map((part) => part.trim());
  const values: Record<string, string | undefined> = {};

  parts.forEach((part) => {
    if (part.startsWith(ZONE_KEY)) {
      values.zone = part.replace(ZONE_KEY, "").trim();
    } else if (part.startsWith(AREA_KEY)) {
      values.area = part.replace(AREA_KEY, "").trim();
    } else if (part.startsWith(CITY_KEY)) {
      values.city = part.replace(CITY_KEY, "").trim();
    }
  });

  return {
    zone: values.zone ?? "",
    area: values.area ?? "",
    city: values.city ?? "",
  };
};

export const parseKuwaitAddressFields = (
  appartment: string,
  building: string,
): {
  number: string | undefined;
  floor: string | undefined;
  building: string | undefined;
  block: string | undefined;
} => {
  const appartmentParts = appartment.split("|").map((part) => part.trim());
  const buildingParts = building.split("|").map((part) => part.trim());

  const values: Record<string, string | undefined> = {};

  appartmentParts.forEach((part) => {
    if (part.startsWith(FLOOR_KEY)) {
      values.floor = part.replace(FLOOR_KEY, "").trim();
    } else if (part.startsWith(FLAT_KEY)) {
      values.number = part.replace(FLAT_KEY, "").trim();
    }
  });

  buildingParts.forEach((part) => {
    if (part.startsWith(BLOCK_KEY)) {
      values.block = part.replace(BLOCK_KEY, "").trim();
    } else {
      values.building = part.trim();
    }
  });

  return {
    number: values.number ?? "",
    floor: values.floor ?? "",
    building: values.building ?? "",
    block: values.block ?? "",
  };
};
